/**
 * assets/pothys-collections.js
 * Pothys — Our Collections Section JS
 *
 * API:  window.PothysCollections.init(sectionId, options)
 * Options:
 *   autoSpeed      {Number}  ms between auto-scroll steps   (default 2000)
 *   cardImageSpeed {Number}  ms between card image slides   (default 1200)
 *   resetOnLeave   {Boolean} reset card image on mouseleave (default true)
 */

(function () {
  'use strict';

  /* ══════════════════════════════════════════════════
     NAMESPACE
  ══════════════════════════════════════════════════ */
  window.PothysCollections = window.PothysCollections || {};

  window.PothysCollections.init = function (sectionId, options) {
    const section = document.getElementById(sectionId);
    if (!section) return;

    const opts = Object.assign({
      autoSpeed:      2000,
      cardImageSpeed: 1200,
      resetOnLeave:   true
    }, options || {});

    /* ── Collect all tab IDs in this section ── */
    const tabPanels = section.querySelectorAll('.pc-tab-panel');
    const tabIds    = Array.from(tabPanels).map(p => p.id);

    /* State maps keyed by tabId */
    const mainPos    = {};
    const autoTimers = {};
    tabIds.forEach(id => { mainPos[id] = 0; });

    /* ════════════════════════════════════════════════
       TAB SWITCHING
    ════════════════════════════════════════════════ */
    const tabBtns = section.querySelectorAll('.pc-tab-btn');
    tabBtns.forEach(btn => {
      btn.addEventListener('click', function () {
        const targetId = this.dataset.tab;

        tabBtns.forEach(b => b.classList.remove('active'));
        tabPanels.forEach(p => p.classList.remove('active'));

        this.classList.add('active');
        const panel = document.getElementById(targetId);
        if (panel) panel.classList.add('active');

        /* Reset carousel + restart auto-scroll */
        stopAuto(targetId);
        mainPos[targetId] = 0;
        applyTransform(targetId);
        updateArrows(targetId);
        updateDots(targetId);
        startAuto(targetId);
      });
    });

    /* ════════════════════════════════════════════════
       MAIN CAROUSEL HELPERS
    ════════════════════════════════════════════════ */
    function getTrack(id) { return document.getElementById('track-' + id); }

    function getCardWidth(id) {
      const track = getTrack(id);
      if (!track) return 0;
      const card = track.querySelector('.pc-product-card');
      if (!card) return 0;
      return card.offsetWidth + 18; /* 18 = gap */
    }

    function getVisibleCount(id) {
      const track = getTrack(id);
      if (!track) return 3;
      const cardW = getCardWidth(id);
      if (!cardW) return 3;
      return Math.max(1, Math.floor(track.parentElement.offsetWidth / cardW));
    }

    function getMax(id) {
      const track = getTrack(id);
      if (!track) return 0;
      const cards = track.querySelectorAll('.pc-product-card');
      return Math.max(0, cards.length - getVisibleCount(id));
    }

    function applyTransform(id) {
      const track = getTrack(id);
      if (!track) return;
      const cardW = getCardWidth(id);
      track.style.transform = 'translateX(-' + (mainPos[id] * cardW) + 'px)';
    }

    function updateArrows(id) {
      const pos = mainPos[id];
      const max = getMax(id);

      const prevFloating = document.getElementById('fl-prev-' + id);
      const nextFloating = document.getElementById('fl-next-' + id);
      if (prevFloating) prevFloating.disabled = pos <= 0;
      if (nextFloating) nextFloating.disabled = pos >= max;
    }

    function updateDots(id) {
      const dotsEl = document.getElementById('cdots-' + id);
      if (!dotsEl) return;

      const total = getMax(id) + 1;

      /* Rebuild if count changed */
      if (dotsEl.children.length !== total) {
        dotsEl.innerHTML = '';
        for (let i = 0; i < total; i++) {
          const d = document.createElement('button');
          d.className = 'pc-c-dot' + (i === 0 ? ' active' : '');
          d.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          d.addEventListener('click', function () {
            goToMain(id, i);
            startAuto(id); /* reset timer on manual click */
          });
          dotsEl.appendChild(d);
        }
      }

      Array.from(dotsEl.children).forEach(function (d, i) {
        d.classList.toggle('active', i === mainPos[id]);
      });
    }

    function goToMain(id, pos) {
      mainPos[id] = Math.max(0, Math.min(getMax(id), pos));
      applyTransform(id);
      updateArrows(id);
      updateDots(id);
    }

    /* Expose slide function so inline onclick still works */
    section._pcSlide = function (id, dir) {
      goToMain(id, mainPos[id] + dir);
      startAuto(id);
    };

    /* ── Wire up floating arrows ── */
    tabIds.forEach(function (id) {
      const prevBtn = document.getElementById('fl-prev-' + id);
      const nextBtn = document.getElementById('fl-next-' + id);

      if (prevBtn) prevBtn.addEventListener('click', function () {
        goToMain(id, mainPos[id] - 1);
        startAuto(id);
      });
      if (nextBtn) nextBtn.addEventListener('click', function () {
        goToMain(id, mainPos[id] + 1);
        startAuto(id);
      });
    });

    /* ════════════════════════════════════════════════
       AUTO-SCROLL
    ════════════════════════════════════════════════ */
    function startAuto(id) {
      clearInterval(autoTimers[id]);
      autoTimers[id] = setInterval(function () {
        const max  = getMax(id);
        const next = mainPos[id] >= max ? 0 : mainPos[id] + 1;
        goToMain(id, next);
      }, opts.autoSpeed);
    }

    function stopAuto(id) {
      clearInterval(autoTimers[id]);
    }

    /* Pause on hover */
    tabIds.forEach(function (id) {
      const wrap = document.getElementById('wrap-' + id);
      if (!wrap) return;
      wrap.addEventListener('mouseenter', function () { stopAuto(id); });
      wrap.addEventListener('mouseleave', function () { startAuto(id); });
    });

    /* ════════════════════════════════════════════════
       CARD IMAGE SLIDER
    ════════════════════════════════════════════════ */
    function goToSlide(card, index) {
      const slides = card.querySelectorAll('.pc-img-slide');
      const dots   = card.querySelectorAll('.pc-dot');
      const segs   = card.querySelectorAll('.pc-progress-seg');
      const total  = slides.length;
      if (total <= 1) return;

      const i = ((index % total) + total) % total;
      card.setAttribute('data-current', i);
      card.querySelector('.pc-img-slides').style.transform = 'translateX(-' + (i * 100) + '%)';
      dots.forEach(function (d, idx) { d.classList.toggle('active', idx === i); });
      segs.forEach(function (s, idx) { s.classList.toggle('active', idx === i); });
    }

    const hoverTimers = new WeakMap();

    section.querySelectorAll('.pc-product-card').forEach(function (card) {

      /* Prev / next card-level buttons */
      const prevBtn = card.querySelector('.pc-slide-prev');
      const nextBtn = card.querySelector('.pc-slide-next');

      if (prevBtn) prevBtn.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        const cur = parseInt(card.getAttribute('data-current') || '0');
        goToSlide(card, cur - 1);
      });
      if (nextBtn) nextBtn.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        const cur = parseInt(card.getAttribute('data-current') || '0');
        goToSlide(card, cur + 1);
      });

      /* Dot clicks */
      card.querySelectorAll('.pc-dot').forEach(function (dot) {
        dot.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          goToSlide(card, parseInt(this.dataset.index || '0'));
        });
      });

      /* Progress segment clicks */
      card.querySelectorAll('.pc-progress-seg').forEach(function (seg) {
        seg.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          goToSlide(card, parseInt(this.dataset.index || '0'));
        });
      });

      /* Auto-cycle on hover */
      const total = card.querySelectorAll('.pc-img-slide').length;

      card.addEventListener('mouseenter', function () {
        if (total <= 1) return;
        const t = setInterval(function () {
          const cur = parseInt(card.getAttribute('data-current') || '0');
          goToSlide(card, cur + 1);
        }, opts.cardImageSpeed);
        hoverTimers.set(card, t);
      });

      card.addEventListener('mouseleave', function () {
        clearInterval(hoverTimers.get(card));
        if (opts.resetOnLeave) goToSlide(card, 0);
      });
    });

    /* ════════════════════════════════════════════════
       WISHLIST TOGGLE
    ════════════════════════════════════════════════ */
    section.querySelectorAll('.pc-wishlist-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        this.classList.toggle('wishlisted');

        /* Optional: integrate with Shopify Wishlist / custom app */
        const productId = this.dataset.productId;
        if (productId) {
          document.dispatchEvent(new CustomEvent('pothys:wishlist:toggle', {
            detail: { productId: productId, wishlisted: this.classList.contains('wishlisted') }
          }));
        }
      });
    });

    /* ════════════════════════════════════════════════
       ADD TO CART (AJAX)
    ════════════════════════════════════════════════ */
    section.querySelectorAll('[data-add-to-cart]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        const variantId = parseInt(this.dataset.variantId);
        if (!variantId) return;

        const originalText = this.textContent;
        this.disabled = true;
        this.textContent = 'Adding…';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
        .then(function (r) { return r.json(); })
        .then(function () {
          btn.textContent = '✓ Added';
          document.dispatchEvent(new CustomEvent('cart:updated'));
          setTimeout(function () {
            btn.disabled = false;
            btn.textContent = originalText;
          }, 1800);
        })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = originalText;
        });
      });
    });

    /* ════════════════════════════════════════════════
       RESIZE HANDLER
    ════════════════════════════════════════════════ */
    let resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        tabIds.forEach(function (id) {
          applyTransform(id);
          updateArrows(id);
          updateDots(id);
        });
      }, 120);
    });

    /* ════════════════════════════════════════════════
       INIT — start auto-scroll for active tab only
    ════════════════════════════════════════════════ */
    tabIds.forEach(function (id) {
      updateArrows(id);
      updateDots(id);

      /* Only start auto on the visible (active) tab */
      const panel = document.getElementById(id);
      if (panel && panel.classList.contains('active')) {
        startAuto(id);
      }
    });

    /* Shopify Theme Editor live-reload support */
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', function (e) {
        if (e.detail.sectionId === sectionId) {
          tabIds.forEach(function (id) {
            updateArrows(id);
            updateDots(id);
          });
        }
      });
    }
  };

})();
